import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { TRIAL_DAYS } from "@/config/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CODE_KEY = "creditcost_code_pro";
const TRIAL_KEY = "creditcost_trial_start";
const TRIAL_MS = TRIAL_DAYS * 86400000;

const ProContext = createContext(null);

export function ProProvider({ children }) {
  const [codeUnlock, setCodeUnlock] = useState(false);
  const [trialStart, setTrialStart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    setCodeUnlock(localStorage.getItem(CODE_KEY) === "1");
    let start = parseInt(localStorage.getItem(TRIAL_KEY) || "", 10);
    if (!Number.isFinite(start)) {
      start = Date.now();
      localStorage.setItem(TRIAL_KEY, String(start));
    }
    setTrialStart(start);
    setLoading(false);
  }, []);

  // keep trial countdown fresh (once a minute)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Validate a typed-in code against the backend master code.
  const redeemCode = useCallback(async (code) => {
    const trimmed = (code || "").trim();
    if (!trimmed) return false;
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/validate-code`, { code: trimmed });
      const ok = !!res.data?.valid;
      if (ok) {
        setCodeUnlock(true);
        localStorage.setItem(CODE_KEY, "1");
      }
      return ok;
    } catch {
      return false;
    } finally {
      setRedeeming(false);
    }
  }, []);

  const clearLocalUnlock = useCallback(() => {
    setCodeUnlock(false);
    localStorage.setItem(CODE_KEY, "0");
  }, []);

  const trialActive = trialStart != null && now < trialStart + TRIAL_MS;
  const trialDaysLeft =
    trialStart != null
      ? Math.min(TRIAL_DAYS, Math.max(0, Math.ceil((trialStart + TRIAL_MS - now) / 86400000)))
      : 0;
  const trialEnded = trialStart != null && !trialActive;
  const paid = codeUnlock;
  const isPro = paid || trialActive;

  const value = useMemo(
    () => ({
      isPro,
      paid,
      codeUnlock,
      loading,
      redeeming,
      trialActive,
      trialDaysLeft,
      trialEnded,
      redeemCode,
      clearLocalUnlock,
    }),
    [isPro, paid, codeUnlock, loading, redeeming, trialActive, trialDaysLeft, trialEnded, redeemCode, clearLocalUnlock],
  );

  return <ProContext.Provider value={value}>{children}</ProContext.Provider>;
}

export function usePro() {
  const ctx = useContext(ProContext);
  if (!ctx) throw new Error("usePro must be used within ProProvider");
  return ctx;
}
